import React, { useMemo } from "react";
import {
  MapPin, AlertTriangle, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { EventPopover } from "@/components/EventPopover";
import {
  ROLES,
  isStudioJob,
  getMinStaff,
  getTodayDate,
  extractVenue,
} from "@/utils/constants";

export const RotaWeekView = ({
  weekStart,
  onPrevWeek,
  onNextWeek,
  onGoToThisWeek,
  calendarJobsByDate,
  teamMembers,
  unavailableByDate,
  getDisplayTitle,
  onOpenJobSheet,
  sheetStatuses,
  editMode,
  onHideJob,
  onDeleteJob,
}) => {
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(weekStart.getDate() + 6);
    const opts = { day: 'numeric', month: 'short' };
    const startStr = weekStart.toLocaleDateString('en-GB', opts);
    const endStr = end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
    return `${startStr} — ${endStr}`;
  }, [weekStart]);

  const formatDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const todayDate = getTodayDate();

  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden" data-testid="week-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] bg-[#f9fafb]">
        <div className="flex items-center gap-2">
          <button onClick={onPrevWeek} className="p-1 rounded hover:bg-gray-200 transition-colors" data-testid="week-prev">
            <ChevronLeft className="h-5 w-5 text-[#605e5e]" />
          </button>
          <button onClick={onNextWeek} className="p-1 rounded hover:bg-gray-200 transition-colors" data-testid="week-next">
            <ChevronRight className="h-5 w-5 text-[#605e5e]" />
          </button>
          <h2 className="font-heading text-lg text-[#605e5e] ml-2">{weekLabel}</h2>
        </div>
        <button onClick={onGoToThisWeek} className="text-xs px-3 py-1 rounded border border-[#e5e7eb] text-[#605e5e] hover:bg-gray-100 transition-colors" data-testid="week-today">
          This Week
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-7">
        {weekDays.map((day) => {
          const dateStr = formatDateStr(day);
          const dayJobs = calendarJobsByDate[dateStr] || [];
          const isToday = dateStr === todayDate;
          const dayUnavail = unavailableByDate[dateStr] || [];
          const dayName = day.toLocaleDateString('en-GB', { weekday: 'short' });
          const dayNum = day.getDate();
          const monthName = day.toLocaleDateString('en-GB', { month: 'short' });

          return (
            <div
              key={dateStr}
              className={`border-r border-b border-[#e5e7eb] last:border-r-0 sm:min-h-[300px] ${isToday ? 'bg-[#f0fdf4]' : 'bg-white'}`}
              data-testid={`week-day-${dateStr}`}
            >
              <div className={`px-2 py-2 border-b border-[#e5e7eb] ${isToday ? 'bg-[#605e5e]' : 'bg-[#f9fafb]'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-medium ${isToday ? 'text-white' : 'text-[#8d8b8b]'}`}>{dayName}</span>
                    <span className={`text-sm font-semibold ${isToday ? 'text-white' : 'text-[#605e5e]'}`}>{dayNum}</span>
                    <span className={`text-xs ${isToday ? 'text-white/70' : 'text-[#8d8b8b]'}`}>{monthName}</span>
                  </div>
                  {dayJobs.length > 0 && (
                    <Badge className={`text-[10px] px-1.5 py-0 ${isToday ? 'bg-white/20 text-white border-none' : 'bg-[#605e5e]/10 text-[#605e5e] border-none'}`}>
                      {dayJobs.length}
                    </Badge>
                  )}
                </div>
                {dayUnavail.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {dayUnavail.map((person) => (
                      <span key={person} className={`text-[9px] leading-tight px-1 py-0 rounded ${isToday ? 'bg-red-400/30 text-white' : 'bg-red-100 text-red-700'}`}>
                        {person} off
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-1.5 space-y-1.5 overflow-y-auto max-h-[400px]">
                {dayJobs.length === 0 && (
                  <p className="text-[10px] text-[#c4c4c4] text-center py-4">No jobs</p>
                )}
                {dayJobs.map(job => {
                  const minStaff = getMinStaff(job.event_name || job.title);
                  const assigned = ROLES.filter(r => job.assignments?.[r]).length;
                  const understaffed = minStaff > 0 && assigned < minStaff && !job.staffing_confirmed;
                  const members = ROLES.map(r => job.assignments?.[r]).filter(Boolean).map(id => teamMembers.find(m => m.id === id)?.name).filter(Boolean);
                  return (
                    <Popover key={job.id}>
                      <PopoverTrigger asChild>
                        <div
                          className={`rounded-md p-2 cursor-pointer hover:ring-1 transition-all text-left ${
                            isStudioJob(job)
                              ? 'bg-violet-50 border border-violet-200 hover:ring-violet-300/40'
                              : understaffed
                                ? 'bg-red-50 border border-red-200 hover:ring-[#8DA399]/40'
                                : minStaff > 0 && assigned >= minStaff
                                  ? 'bg-[#8DA399]/10 border border-[#8DA399]/20 hover:ring-[#8DA399]/40'
                                  : 'bg-gray-50 border border-gray-200 hover:ring-[#8DA399]/40'
                          }`}
                          data-testid={`week-event-${job.id}`}
                        >
                          <p className="text-xs font-medium text-[#1f2937] leading-tight truncate">{getDisplayTitle(job)}</p>
                          {job.time && <p className="text-[10px] text-[#8d8b8b] mt-0.5">{job.time}</p>}
                          {job.location && (
                            <p className="text-[10px] text-[#6b7280] mt-0.5 flex items-center gap-0.5 truncate">
                              <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                              {extractVenue(job.location)}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {members.length > 0 ? members.map((name) => (
                              <span key={name} className="text-[9px] px-1 py-0 rounded bg-[#8DA399] text-white">{name}</span>
                            )) : (
                              <span className="text-[9px] text-[#c4c4c4]">Unassigned</span>
                            )}
                          </div>
                          {understaffed && (
                            <div className="flex items-center gap-0.5 mt-1">
                              <AlertTriangle className="h-2.5 w-2.5 text-red-500" />
                              <span className="text-[9px] text-red-600">{assigned}/{minStaff} staff</span>
                            </div>
                          )}
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" side="right" align="start">
                        <EventPopover
                          job={job}
                          members={members}
                          teamMembers={teamMembers}
                          understaffed={understaffed}
                          assigned={assigned}
                          minStaff={minStaff}
                          dayUnavail={dayUnavail}
                          getDisplayTitle={getDisplayTitle}
                          onOpenJobSheet={onOpenJobSheet}
                          sheetStatus={sheetStatuses?.[job.id]}
                          editMode={editMode}
                          onHideJob={onHideJob}
                          onDeleteJob={onDeleteJob}
                        />
                      </PopoverContent>
                    </Popover>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
